@use '@/styles/tokens' as *;

.content {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: var(--surface-card);
}

// Opt-in (`roundedBody`): the header's cream shows through instead of another white, so `.bodyCard`'s
// rounded top corners have something to reveal.
.contentFramed {
  background: var(--surface-page);
}

// `flex: 0 0 auto`, never the default `flex-shrink: 1`: `.body`'s basis is its content height, so a
// long body (a replacement request's description) squeezed the header and `overflow: hidden` clipped
// its subtitle — the order number and status chip (bug 120881). Only the body scrolls.
.header {
  flex: 0 0 auto;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  // Each inset reads a custom property defaulting to the shared value, so one frame whose spacing
  // differs (Edit Profile, Figma 1683:589929) can set it through `<Modal className>`.
  padding: var(--overlay-header-padding, 24px 24px 16px);
  background: var(--surface-page);
  overflow: hidden;
}

// Stacks the title with its optional subtitle. Carries the z-index so the pair sits above the
// decorative pattern; `.title` keeps its own for the overlays that render a bare title.
.titleGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  z-index: 1;
}

.title {
  margin: 0;
  font-family: $font-family-heading;
  font-size: $font-size-2xl;
  font-weight: $font-weight-regular;
  letter-spacing: -0.02em;
  color: var(--text-primary);
  z-index: 1;
}

// Figma 403:275449 — the instruction line under a wizard's title.
.subtitle {
  margin: 0;
  font-size: $font-size-md;
  color: var(--text-muted);
}

.pattern {
  pointer-events: none;
  user-select: none;
  position: absolute;
  inset-inline: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.9;
  z-index: 0;
}

.close {
  position: absolute;
  top: var(--overlay-close-top, 16px);
  inset-inline-end: var(--overlay-close-end, 16px);
  z-index: 1;
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--overlay-body-padding, 24px);
}

// Opt-in (`flushBody`): the child owns the padding and the scrolling. For content that bleeds to the
// card's edges and scrolls a pane of its own rather than the whole body.
// **No current consumer** — the Add Parameters wizard's Preview step was the only one, and ticket 14
// replaced its edge-to-edge cream tab rail with a plain confirmation panel that wants the padded body
// back. Kept because the prop is shared and tested and a next consumer is cheap; delete it if none
// arrives. A negative-margin bleed is not an alternative for that consumer: `overflow-y: auto` above
// makes `overflow-x` compute to `auto` too, so anything hanging off the left becomes unreachable
// overflow and is clipped.
.bodyFlush {
  padding: 0;
  overflow: hidden;
}

// Opt-in (`roundedBody`): the top-rounded white card treatment (Figma `1672:456851` — closest
// project token to the file's measured 20px is `$radius-xl`, the same token `<Modal>`'s own outer
// corners already use for an overlay card). Only the top corners round — the card runs flush to
// `.content`'s bottom edge, same as the Figma layer it matches.
.bodyCard {
  background: var(--surface-card);
  border-radius: $radius-xl $radius-xl 0 0;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: var(--overlay-footer-padding, 16px 24px 24px);
}

.footerCenter {
  justify-content: center;
  background: var(--surface-page);
}

.footerLeft {
  margin-inline-end: auto;
  display: flex;
  align-items: center;
}

.danger {
  --ant-color-primary: var(--status-danger-strong);
  background: var(--status-danger-strong) !important;
  border-color: var(--status-danger-strong) !important;
  color: var(--text-inverse) !important;
}

.form {
  display: contents;
}

.footerRegion {
  flex: 0 0 auto;
}
